// Gift Message Modal JavaScript
// Use window.GiftMessageModal to allow redeclaration
window.GiftMessageModal = class GiftMessageModal {
  constructor() {
    console.log('GiftMessageModal constructor called');
    
    this.modal = document.getElementById('giftMessageModal');
    this.messageTextarea = document.getElementById('giftMessage');
    this.charCountElement = document.getElementById('charCount');
    this.occasionSelect = document.getElementById('occasion');
    this.presetContainer = document.getElementById('giftPresetPills');
    this.presetData = null;
    this.isDisabled = false;
    this.mode = 'gift_upsell'; // 'gift_only' | 'upsell_only' | 'gift_upsell'
    this.selectedExtraItems = []; // Initialize selected extra items array
    
    // Greeting card state management
    this.selectedCardDesign = null;
    this.selectedCardVariantId = null;
    this.cardLanguage = 'english';
    this.isGreetingCardFlow = false; // Track which flow customer chose
    this.greetingCardMessage = document.getElementById('greetingCardMessage');
    this.cardCharCountElement = document.getElementById('cardCharCount');
    
    // Accessibility properties
    this.previousActiveElement = null;
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;
    this.liveRegion = null;
    
    // Check if modal element exists before initializing
    if (!this.modal) {
      console.error('Gift message modal element not found in constructor - modal will not work');
      return;
    }
    
    this.init();
  }

  init() {
    console.log('Gift message modal init started');
    console.log('Modal element:', this.modal);
    console.log('Message textarea:', this.messageTextarea);
    console.log('Occasion select:', this.occasionSelect);
    console.log('Preset container:', this.presetContainer);
    
    if (!this.modal) {
      console.error('Gift message modal element not found - modal will not work');
      return;
    }

    console.log('Gift message modal initialized successfully');

    // Initialize accessibility features
    this.createLiveRegion();
    this.setupAccessibility();

    // Character count functionality and real-time validation
    if (this.messageTextarea && this.charCountElement) {
      this.messageTextarea.addEventListener('input', () => {
        this.updateCharCount();
        this.clearErrors(); // Clear errors as user types
      });
      
      // Add blur validation
      this.messageTextarea.addEventListener('blur', () => {
        if (this.messageTextarea.value.trim()) {
          this.validateForm();
        }
      });
    }

    // Greeting card message character count functionality
    if (this.greetingCardMessage && this.cardCharCountElement) {
      this.greetingCardMessage.addEventListener('input', () => {
        this.updateCardCharCount();
      });
    }

    // Close modal on overlay click
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.close();
      }
    });

    // Enhanced keyboard navigation
    this.modal.addEventListener('keydown', (e) => this.handleKeydown(e));

    // Set up add to cart interception
    this.setupAddToCartInterception();

    // Load preset data and set up occasion listener
    this.loadPresetData();
    this.setupOccasionListener();
  }

  setDisabled(disabled) {
    this.isDisabled = disabled;
    console.log('Gift message modal disabled:', disabled);
  }

  // Accessibility Methods
  createLiveRegion() {
    // Create a live region for screen reader announcements
    this.liveRegion = document.createElement('div');
    this.liveRegion.setAttribute('aria-live', 'polite');
    this.liveRegion.setAttribute('aria-atomic', 'true');
    this.liveRegion.className = 'sr-only';
    this.liveRegion.style.cssText = `
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    `;
    document.body.appendChild(this.liveRegion);
  }

  announceToScreenReader(message) {
    if (this.liveRegion) {
      this.liveRegion.textContent = message;
    }
  }

  setupAccessibility() {
    // Set up ARIA attributes
    if (this.modal) {
      this.modal.setAttribute('aria-labelledby', 'giftModalHeader');
      this.modal.setAttribute('aria-describedby', 'giftModalDescription');
    }

    // Add description element if it doesn't exist
    if (!document.getElementById('giftModalDescription')) {
      const description = document.createElement('div');
      description.id = 'giftModalDescription';
      description.className = 'sr-only';
      description.textContent = 'Add a personalized gift message to your order';
      if (this.modal) {
        this.modal.insertBefore(description, this.modal.firstChild);
      }
    }
  }

  getFocusableElements() {
    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
      '.preset-pill'
    ];
    
    return this.modal.querySelectorAll(focusableSelectors.join(', '));
  }

  trapFocus() {
    this.focusableElements = Array.from(this.getFocusableElements());
    this.firstFocusableElement = this.focusableElements[0];
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];

    // Focus the first element
    if (this.firstFocusableElement) {
      this.firstFocusableElement.focus();
    }
  }

  handleKeydown(e) {
    if (!this.modal || this.modal.style.display !== 'flex') return;

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        this.close();
        break;
      
      case 'Tab':
        this.handleTabKey(e);
        break;
      
      case 'ArrowDown':
      case 'ArrowUp':
        if (e.target.classList.contains('preset-pill')) {
          e.preventDefault();
          this.handlePresetNavigation(e);
        }
        break;
      
      case 'Enter':
      case ' ':
        if (e.target.classList.contains('preset-pill')) {
          e.preventDefault();
          this.selectPreset(e.target);
        }
        break;
    }
  }

  handleTabKey(e) {
    if (this.focusableElements.length === 0) return;

    if (e.shiftKey) {
      // Shift + Tab
      if (document.activeElement === this.firstFocusableElement) {
        e.preventDefault();
        this.lastFocusableElement.focus();
      }
    } else {
      // Tab
      if (document.activeElement === this.lastFocusableElement) {
        e.preventDefault();
        this.firstFocusableElement.focus();
      }
    }
  }

  handlePresetNavigation(e) {
    const currentIndex = this.focusableElements.indexOf(e.target);
    if (currentIndex === -1) return;

    let nextIndex;
    if (e.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % this.focusableElements.length;
    } else {
      nextIndex = currentIndex === 0 ? this.focusableElements.length - 1 : currentIndex - 1;
    }

    this.focusableElements[nextIndex].focus();
  }

  selectPreset(presetElement) {
    const message = presetElement.textContent.trim();
    if (this.messageTextarea) {
      this.messageTextarea.value = message;
      this.updateCharCount();
      this.clearErrors();
      this.announceToScreenReader(`Preset message selected: ${message}`);
    }
  }

  // Form validation methods
  validateForm() {
    const message = this.messageTextarea ? this.messageTextarea.value.trim() : '';
    const maxLength = this.messageTextarea ? parseInt(this.messageTextarea.getAttribute('maxlength')) : 250;
    
    this.clearErrors();
    
    if (!message) {
      this.showError('Please enter a gift message.');
      return false;
    }
    
    if (message.length > maxLength) {
      this.showError(`Message is too long. Please keep it under ${maxLength} characters.`);
      return false;
    }
    
    return true;
  }

  showError(message) {
    const errorElement = document.getElementById('message-error');
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.classList.remove('sr-only');
      
      // Set aria-invalid on textarea
      if (this.messageTextarea) {
        this.messageTextarea.setAttribute('aria-invalid', 'true');
      }
      
      // Announce error to screen readers
      this.announceToScreenReader(`Error: ${message}`);
    }
  }

  clearErrors() {
    const errorElement = document.getElementById('message-error');
    if (errorElement) {
      errorElement.textContent = '';
      errorElement.classList.add('sr-only');
    }
    
    // Remove aria-invalid from textarea
    if (this.messageTextarea) {
      this.messageTextarea.removeAttribute('aria-invalid');
    }
  }

  setupAddToCartInterception() {
    console.log('Setting up add to cart interception...');
    
    // Listen for product-form submit events
    document.addEventListener('submit', async (e) => {
      const form = e.target;
      if (!form.closest('product-form')) return;
      
      // Check if we have selected extra items
      if (window.selectedExtraItems && window.selectedExtraItems.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        
        try {
          const formData = new FormData(form);
          const variantId = formData.get('id');
          const quantity = parseInt(formData.get('quantity') || '1');
          // Collect personalization and gift properties from DOM
          const properties = this.getFormProperties ? this.getFormProperties() : {};
          
          if (!variantId) {
            console.error('No variant ID found in form');
            form.submit(); // Fallback to normal submission
            return;
          }
          
          // Build items array
          const items = [{
            id: variantId,
            quantity: quantity,
            // Only include properties if we have any keys
            ...(properties && Object.keys(properties).length > 0 ? { properties } : {})
          }];
          
          // Add selected extra items
          window.selectedExtraItems.forEach(extraItem => {
            items.push({
              id: extraItem.variantId,
              quantity: 1
            });
          });
          
          // Add to cart
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ items })
          });
          
          if (!response.ok) {
            throw new Error('Failed to add items to cart');
          }
          
          const data = await response.json();
          
          // Clear selected items after successful add
          window.selectedExtraItems = [];
          
          // Update button state
          this.updateUpsellButtonState();
          
          // Trigger cart update event
          document.dispatchEvent(new CustomEvent('cart:updated', { 
            detail: { items: data.items } 
          }));
          
          // Update cart UI using product-form's method
          const productForm = form.closest('product-form');
          if (productForm && productForm.cartDrawer) {
            productForm.cartDrawer.renderContents(data, true);
          }
          
          // Update cart icon
          if (typeof ProductForm !== 'undefined' && ProductForm.updateCartIcon) {
            ProductForm.updateCartIcon(data);
          }
          
        } catch (error) {
          console.error('Error adding to cart:', error);
          alert('There was an error adding the item to your cart. Please try again.');
          form.submit(); // Fallback to normal submission
        }
      }
    }, true); // Use capture phase
    
    // Set up event delegation on document to catch clicks (safer than body)
    const handleClick = (e) => {
      // Check if clicked element is an add to cart button
      const target = e.target.closest('button[name="add"], .product-form__cart-submit, .btn--atc, [data-add-to-cart]');
      if (target && this.isAddToCartButton(target)) {
        console.log('🔥 Add to cart click intercepted!');
        if (!this.isDisabled && this.modal) {
          console.log('⏸️ Preventing default cart action');
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          console.log('🎉 Showing gift message modal');
          this.show();
          return false;
        }
      }
    };
    
    // Add event listener with error handling
    try {
      document.addEventListener('click', handleClick, true);
    } catch (error) {
      console.warn('Could not set up document click listener:', error);
      // Fallback to specific button binding
      setTimeout(() => this.attemptButtonInterception(0), 100);
      return;
    }
    
    // Also set up specific button interception as backup
    setTimeout(() => this.attemptButtonInterception(0), 50);
  }
  
  isAddToCartButton(button) {
    // Check if this looks like an add to cart button
    const buttonText = button.textContent?.toLowerCase() || '';
    const buttonName = button.getAttribute('name') || '';
    const buttonClass = button.className || '';
    
    return (
      buttonName === 'add' ||
      buttonClass.includes('cart-submit') ||
      buttonClass.includes('btn--atc') ||
      button.hasAttribute('data-add-to-cart') ||
      buttonText.includes('add to cart') ||
      buttonText.includes('add to bag') ||
      (button.type === 'submit' && button.closest('.product-form'))
    );
  }
  
  attemptButtonInterception(attempt) {
    if (attempt > 3) {
      console.log('❌ Add to cart button not found after multiple attempts');
      return;
    }
    
    console.log(`Attempting button interception (attempt ${attempt + 1})...`);
    
    // Try multiple selectors to find the add to cart button
    const selectors = [
      'button[name="add"]',
      '.product-form__cart-submit',
      '.btn--atc',
      '.product-form button[type="submit"]',
      'button[data-add-to-cart]'
    ];

    let addToCartBtn = null;
    
    for (const selector of selectors) {
      const elements = document.querySelectorAll(selector);
      if (elements.length > 0) {
        addToCartBtn = elements[0];
        console.log('✅ Found add to cart button with selector:', selector);
        break;
      }
    }

    if (!addToCartBtn) {
      // Retry with exponential backoff
      const delay = Math.pow(2, attempt) * 100; // 100ms, 200ms, 400ms
      console.log(`❌ Button not found, retrying in ${delay}ms...`);
      setTimeout(() => this.attemptButtonInterception(attempt + 1), delay);
      return;
    }

    // Add specific button interception as backup to delegation
    try {
      addToCartBtn.addEventListener('click', (e) => {
        console.log('🔥 Specific add to cart clicked!');
        
        if (!this.isDisabled && this.modal) {
          console.log('⏸️ Preventing default cart action (specific)');
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          console.log('🎉 Showing gift message modal (specific)');
          this.show();
          return false;
        }
      }, true);
      
      console.log('✅ Specific add to cart interception setup complete');
    } catch (error) {
      console.warn('Could not add specific button listener:', error);
    }
  }

  updateCharCount() {
    const currentLength = this.messageTextarea.value.length;
    const maxLength = this.messageTextarea.getAttribute('maxlength');
    
    this.charCountElement.textContent = currentLength;
    
    // Update styling based on character count
    const remaining = maxLength - currentLength;
    this.charCountElement.classList.remove('warning', 'danger');
    
    if (remaining <= 50) {
      this.charCountElement.classList.add('warning');
    }
    if (remaining <= 20) {
      this.charCountElement.classList.add('danger');
    }
  }

  updateCardCharCount() {
    if (!this.greetingCardMessage || !this.cardCharCountElement) return;
    
    const currentLength = this.greetingCardMessage.value.length;
    const maxLength = this.greetingCardMessage.getAttribute('maxlength');
    
    this.cardCharCountElement.textContent = currentLength;
    
    // Update styling based on character count
    const remaining = maxLength - currentLength;
    this.cardCharCountElement.classList.remove('warning', 'danger');
    
    if (remaining <= 100) {
      this.cardCharCountElement.classList.add('warning');
    }
    if (remaining <= 50) {
      this.cardCharCountElement.classList.add('danger');
    }
  }

  loadPresetData() {
    const dataElement = document.getElementById('giftPresetData');
    if (dataElement) {
      try {
        console.log('Raw preset data:', dataElement.textContent);
        this.presetData = JSON.parse(dataElement.textContent);
        console.log('Parsed preset data:', this.presetData);
      } catch (e) {
        console.error('Error parsing preset data:', e);
        console.error('Raw content:', dataElement.textContent);
      }
    } else {
      console.error('Preset data element not found');
    }
  }

  setupOccasionListener() {
    console.log('Setting up occasion listener, occasionSelect:', this.occasionSelect);
    if (this.occasionSelect) {
      this.occasionSelect.addEventListener('change', (e) => {
        console.log('Occasion changed to:', e.target.value);
        this.updatePresetsForOccasion(e.target.value);
      });
      
      // Also trigger on initial load if there's already a value
      if (this.occasionSelect.value) {
        console.log('Initial occasion value:', this.occasionSelect.value);
        this.updatePresetsForOccasion(this.occasionSelect.value);
      }
    } else {
      console.error('Occasion select element not found');
    }
  }

  updatePresetsForOccasion(occasion) {
    console.log('Updating presets for occasion:', occasion);
    console.log('Preset container:', this.presetContainer);
    console.log('Preset data:', this.presetData);
    
    if (!this.presetContainer || !this.presetData) {
      console.error('Missing preset container or data');
      return;
    }

    const label = this.presetContainer.querySelector('.preset-label');
    const hint = this.presetContainer.querySelector('.preset-occasion-hint');
    
    // Remove existing preset pills
    const existingPills = this.presetContainer.querySelectorAll('.preset-pill');
    existingPills.forEach(pill => pill.remove());

    if (!occasion) {
      console.log('No occasion selected');
      // Show hint if no occasion selected
      if (hint) hint.style.display = 'inline';
      return;
    }

    // Find all matching messages for the selected occasion
    const foundMessages = [];
    
    console.log('Searching for occasion:', occasion);
    console.log('Available preset data:', this.presetData);
    
    // Create comprehensive mapping for all occasions to their possible variations
    const occasionMappings = {
      'mothers_day': ['Mother\'s Day', 'Mothers Day', 'mothers_day', 'mothers day', 'mother\'s day', 'Mother Day'],
      'valentines': ['Valentine\'s Day', 'Valentines Day', 'valentines', 'valentine', 'valentine\'s day', 'Valentine Day'],
      'fathers_day': ['Father\'s Day', 'Fathers Day', 'fathers_day', 'fathers day', 'father\'s day', 'Father Day'],
      'womens_day': ['Women\'s Day', 'Womens Day', 'womens_day', 'womens day', 'women\'s day', 'Woman Day'],
      'secretarys_day': ['Secretary\'s Day', 'Secretarys Day', 'secretarys_day', 'secretary day', 'secretary\'s day'],
      'teachers_day': ['Teacher\'s Day', 'Teachers Day', 'teachers_day', 'teachers day', 'teacher\'s day'],
      'get_well': ['Get Well Soon', 'Get Well', 'get_well', 'get well', 'Get-Well', 'GetWell'],
      'thank_you': ['Thank You', 'thank_you', 'thank you', 'Thanks', 'Thank-You'],
      'just_because': ['Just Because', 'just_because', 'just because', 'Just-Because'],
      'birthday': ['Birthday', 'birthday', 'Birth Day', 'birth day', 'B-Day', 'bday'],
      'anniversary': ['Anniversary', 'anniversary', 'anniversaries', 'Anniversary Day'],
      'congratulations': ['Congratulations', 'congratulations', 'congrats', 'Congrats', 'Congratulation'],
      'wedding': ['Wedding', 'wedding', 'Wedding Day', 'wedding day', 'Marriage'],
      'christmas': ['Christmas', 'christmas', 'Xmas', 'Christmas Day', 'Holiday'],
      'sympathy': ['Sympathy', 'Condolence', 'sympathy', 'condolence', 'Sympathy/Condolence', 'Condolences'],
      'love': ['Love & Romance', 'Love', 'Romance', 'love', 'romance', 'Love and Romance'],
      'other': ['Other', 'other', 'Custom', 'custom']
    };
    
    // Get possible variations for this occasion
    let searchTerms = occasionMappings[occasion] || [];
    
    // Add generic variations as additional fallbacks
    searchTerms = searchTerms.concat([
      occasion, // exact match
      occasion.charAt(0).toUpperCase() + occasion.slice(1), // capitalize first letter
      occasion.toLowerCase(), // lowercase  
      occasion.toUpperCase(), // uppercase
      occasion.replace(/_/g, ' '), // replace underscores with spaces
      occasion.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), // title case
      occasion.replace(/-/g, ' '), // replace dashes with spaces
      occasion.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) // title case with dashes
    ]);

    console.log('Search terms:', searchTerms);

    // Search through ALL preset data for ANY matches with the search terms
    for (const handle in this.presetData) {
      const preset = this.presetData[handle];
      console.log(`Checking handle: ${handle}, occasion: ${preset.occasion}, message: ${preset.message}`);
      
      // Check if this preset's occasion matches any of our search terms
      for (const term of searchTerms) {
        if (preset.occasion && (
            preset.occasion.toLowerCase().includes(term.toLowerCase()) || 
            term.toLowerCase().includes(preset.occasion.toLowerCase())
        )) {
          foundMessages.push({ 
            key: handle, 
            occasion: preset.occasion,
            message: preset.message 
          });
          console.log(`✅ Match found: ${handle} - ${preset.occasion}`);
          break; // Break inner loop to avoid duplicates from same handle
        }
      }
    }
    
    // Remove duplicates
    const uniqueMessages = foundMessages.filter((item, index, self) => 
      index === self.findIndex(t => t.message === item.message)
    );
    
    if (uniqueMessages.length === 0) {
      console.log('No data found for occasion:', occasion);
      // Show hint if no data found
      if (hint) hint.style.display = 'inline';
      return;
    }

    // Hide hint when presets are available
    if (hint) hint.style.display = 'none';

    console.log('Found', uniqueMessages.length, 'unique messages for', occasion);
    
    console.log('Final unique messages found:', uniqueMessages);
    
    // Create pills for each unique message
    uniqueMessages.forEach((item, index) => {
      if (item.message && item.message.trim() !== '') {
        console.log(`Adding pill ${index + 1} for message:`, item.message);
        
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'preset-pill';
        pill.textContent = `Option ${index + 1}`;
        pill.title = item.message; // Show full message on hover
        pill.setAttribute('aria-label', `Select preset message: ${item.message}`);
        pill.setAttribute('tabindex', '0');
        pill.setAttribute('role', 'button');
        pill.onclick = () => selectPreset(item.message);
        this.presetContainer.appendChild(pill);
        console.log(`✅ Added pill: Option ${index + 1} with message:`, item.message);
      }
    });
    
    console.log(`🎉 Total pills created: ${uniqueMessages.length}`);
  }


  show(options = {}) {
    console.log('🚀 show() method called');
    console.log('Options passed:', options);
    console.log('Current mode before update:', this.mode);
    console.log('Current cart item key:', this.currentCartItemKey);
    
    // Check if modal is properly initialized
    if (!this.modal) {
      console.error('❌ Cannot show modal - modal element not found or not initialized');
      return;
    }
    
    // Allow callers to specify mode
    if (options.mode) {
      console.log('🎯 Setting mode from options:', options.mode);
      this.mode = options.mode;
    } else {
      console.log('🎯 Using existing mode:', this.mode);
    }

    // Message-only products (set per product type in the section settings):
    // always run the plain gift message flow, never card or upsell steps
    if (this.modal.dataset.messagesOnly === 'true') {
      console.log('🎯 Message-only product type: forcing gift_only mode');
      this.mode = 'gift_only';
    }
    console.log('✅ Final mode:', this.mode);
    console.log('Modal element:', this.modal);
    console.log('Modal current display:', this.modal ? this.modal.style.display : 'no modal');
    
    // Store the currently focused element for restoration
    this.previousActiveElement = document.activeElement;
    
    this.modal.style.display = 'flex';
    this.modal.classList.add('show');
    this.modal.setAttribute('aria-hidden', 'false');
    
    // Sync any pre-selected items from the "Add Something Extra" button
    // This ensures items selected before opening the modal are preserved
    if (window.selectedExtraItems && window.selectedExtraItems.length > 0) {
      console.log('🔄 Syncing pre-selected extra items:', window.selectedExtraItems);
      this.selectedExtraItems = [...window.selectedExtraItems];
    }
    
    // Route to appropriate step based on mode
    if (this.mode === 'upsell_only') {
      this.showStep2();
    } else if (this.mode === 'gift_only') {
      // Cart context - skip Step 0, go directly to Step 1a
      this.showStep1a();
    } else {
      // PDP context - check if greeting card flow is enabled
      const step0Element = document.getElementById('giftStep0');
      if (step0Element) {
        // Greeting card flow enabled - start at Step 0
        this.showStep0();
      } else {
        // Greeting card flow disabled - start at Step 1a
        this.showStep1a();
      }
    }
    
    console.log('Modal should now be visible');
    
    // Set up focus trapping
    setTimeout(() => {
      this.trapFocus();
    }, 100);
    
    // Announce modal opening to screen readers
    this.announceToScreenReader('Gift message modal opened. Use Tab to navigate, Escape to close.');
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
  }

  /**
   * Shows success animation before closing
   */
  showSuccessAnimation() {
    const successElement = document.getElementById('giftMessageSuccess');
    const modalContent = document.querySelector('.gift-message-content');
    if (!successElement) return;
    
    // Hide all steps
    const steps = ['giftStep0', 'giftStep1a', 'giftStep1b', 'giftStep2'];
    steps.forEach(stepId => {
      const step = document.getElementById(stepId);
      if (step) step.style.display = 'none';
    });
    
    // Add class to modal content to maintain height during success animation
    if (modalContent) {
      modalContent.classList.add('showing-success');
    }
    
    // Show success animation
    successElement.style.display = 'flex';
    
    // Announce to screen reader
    this.announceToScreenReader('Items added to cart successfully');
    
    // Close modal after animation - give users time to see confirmation
    setTimeout(() => {
      if (modalContent) {
        modalContent.classList.remove('showing-success');
      }
      this.close();
    }, 3500); // Show for 3.5 seconds
  }

  close() {
    console.log('🚪 close() method called');
    console.trace('Close called from:'); // Show stack trace
    
    // Hide success animation if visible
    const successElement = document.getElementById('giftMessageSuccess');
    if (successElement) {
      successElement.style.display = 'none';
    }
    
    this.modal.style.display = 'none';
    this.modal.classList.remove('show');
    this.modal.setAttribute('aria-hidden', 'true');
    this.showStep1(); // Reset to step 1 (form)
    
    // Reset form
    if (this.messageTextarea) this.messageTextarea.value = '';
    if (this.occasionSelect) this.occasionSelect.value = '';
    if (this.charCountElement) this.charCountElement.textContent = '0';
    
    // Clear cart item tracking
    this.currentCartItemKey = null;
    this.currentCartItemId = null;
    
    // Reset mode to default
    this.mode = 'gift_upsell';
    
    // Restore focus to the element that opened the modal
    if (this.previousActiveElement) {
      this.previousActiveElement.focus();
    }
    
    // Announce modal closing to screen readers
    this.announceToScreenReader('Gift message modal closed.');
    
    // Restore body scroll
    document.body.style.overflow = '';
  }

  showStep1() {
    const step1 = document.getElementById('giftStep1a');
    const step2 = document.getElementById('giftStep2');
    
    if (step1) {
      step1.style.display = 'block';
      step1.classList.add('active');
    }
    if (step2) {
      step2.style.display = 'none';
      step2.classList.remove('active');
    }
    
    // Focus the textarea when showing step 1
    if (this.messageTextarea) {
      this.messageTextarea.focus();
    }
  }

  showStep2() {
    console.log('🎁 showStep2 called');
    const step0 = document.getElementById('giftStep0');
    const step1a = document.getElementById('giftStep1a');
    const step1b = document.getElementById('giftStep1b');
    const step2 = document.getElementById('giftStep2');
    
    console.log('Step elements found:', { step0: !!step0, step1a: !!step1a, step1b: !!step1b, step2: !!step2 });
    
    // Hide all other steps
    if (step0) {
      step0.style.display = 'none';
      step0.classList.remove('active');
    }
    if (step1a) {
      step1a.style.display = 'none';
      step1a.classList.remove('active');
    }
    if (step1b) {
      step1b.style.display = 'none';
      step1b.classList.remove('active');
    }
    
    // Show step 2
    if (step2) {
      step2.style.display = 'block';
      step2.classList.add('active');
    }
    
    // Update button visibility and text based on mode
    this.updateStep2Buttons();
    
    console.log('About to load extra items...');
    console.log('Available collections:', window.upsellModalCollections);
    
    // Load extra items
    this.loadExtraItems();
  }
  
  updateStep2Buttons() {
    const addButton = document.getElementById('step2AddButton');
    const skipButton = document.getElementById('step2SkipButton');
    const backButton = document.getElementById('step2BackButton');
    
    if (!addButton) {
      console.warn('step2AddButton not found');
      return;
    }
    
    if (this.mode === 'upsell_only') {
      // Flow 1: Add Something Extra flow
      // Show "Add Selected" button
      addButton.textContent = 'Add Selected';
      addButton.style.display = 'block';
      
      // Show "No Thanks, Continue" button
      if (skipButton) {
        skipButton.style.display = 'block';
      }
      
      // Hide "Back" button (no previous step in this flow)
      if (backButton) {
        backButton.style.display = 'none';
        backButton.classList.add('hidden');
      }
    } else if (this.mode === 'gift_upsell') {
      // Flow 2: Normal Add to Cart flow
      // Show "Add to Cart" button
      addButton.textContent = 'Add to Cart';
      addButton.style.display = 'block';
      
      // Show "No Thanks, Continue" button
      if (skipButton) {
        skipButton.style.display = 'block';
      }
      
      // Show "Back" button (can go back to Step 1)
      if (backButton) {
        backButton.style.display = 'block';
        backButton.classList.remove('hidden');
      }
    } else if (this.mode === 'gift_only') {
      // Flow 3: Cart editing flow (should not reach here, but handle defensively)
      // Same as gift_upsell for safety
      addButton.textContent = 'Add to Cart';
      addButton.style.display = 'block';
      
      if (skipButton) {
        skipButton.style.display = 'block';
      }
      
      if (backButton) {
        backButton.style.display = 'block';
        backButton.classList.remove('hidden');
      }
    }
  }

  showStep3() {
    // Step 3 no longer exists - redirect to step 2 (extra items)
    this.showStep2();
  }

  showStep0() {
    console.log('🎁 showStep0 called');
    hideStep('giftStep1a');
    hideStep('giftStep1b');
    hideStep('giftStep2');
    showStep('giftStep0');
    
    // Focus the first focusable element in step 0
    setTimeout(() => {
      const firstFocusable = document.querySelector('#giftStep0 button');
      if (firstFocusable) {
        firstFocusable.focus();
      }
    }, 100);
  }

  showStep1a() {
    console.log('🎁 showStep1a called');
    hideStep('giftStep0');
    hideStep('giftStep1b');
    hideStep('giftStep2');
    showStep('giftStep1a');
    
    // Focus the first focusable element in step 1a
    setTimeout(() => {
      const firstFocusable = document.querySelector('#giftStep1a input, #giftStep1a textarea, #giftStep1a select, #giftStep1a button');
      if (firstFocusable) {
        firstFocusable.focus();
      }
    }, 100);
  }

  showStep1b() {
    console.log('🎁 showStep1b called');
    hideStep('giftStep0');
    hideStep('giftStep1a');
    hideStep('giftStep2');
    showStep('giftStep1b');
    resetCardSubSteps();
    
    // Initialize radio button selected state
    setTimeout(() => {
      const checkedRadio = document.querySelector('#giftStep1b input[name="cardLanguage"]:checked');
      if (checkedRadio && checkedRadio.closest('.radio-option')) {
        document.querySelectorAll('#giftStep1b .radio-option').forEach(option => {
          option.classList.remove('selected');
        });
        checkedRadio.closest('.radio-option').classList.add('selected');
      }
    }, 50);
    
    // Load greeting card designs if not already loaded
    if (!this.selectedCardDesign) {
      loadGreetingCardDesigns();
    }
    
    // Focus the first focusable element in step 1b
    setTimeout(() => {
      const firstFocusable = document.querySelector('#giftStep1b input, #giftStep1b textarea, #giftStep1b select, #giftStep1b button');
      if (firstFocusable) {
        firstFocusable.focus();
      }
    }, 100);
  }

  loadExtraItems() {
    console.log('🎁 loadExtraItems called');
    const dropdown = document.getElementById('customDropdown');
    const carouselContainer = document.getElementById('giftExtraCarousel');
    
    console.log('Elements found:', { dropdown: !!dropdown, carouselContainer: !!carouselContainer });
    
    if (!dropdown || !carouselContainer) {
      console.error('❌ Required elements not found');
      return;
    }

    // Get collections from upsell modal settings (passed from Liquid template)
    const upsellCollections = window.upsellModalCollections || null;
    
    console.log('Upsell collections:', upsellCollections);
    
    if (!upsellCollections || upsellCollections.length === 0) {
      console.log('No upsell collections configured - step 3 will not show');
      dropdown.style.display = 'none';
      carouselContainer.innerHTML = '<p style="text-align: center; color: #666;">No extra items available at this time.</p>';
      return;
    }

    // Initialize selected items array
    if (!this.selectedExtraItems) {
      this.selectedExtraItems = [];
    }

    console.log('About to render dropdown with collections:', upsellCollections);
    // Render dropdown
    this.renderExtraDropdown(upsellCollections);
    
    // Load first collection by default
    if (upsellCollections.length > 0) {
      this.currentExtraCollection = upsellCollections[0];
      console.log('Loading first collection:', this.currentExtraCollection);
      this.loadExtraCollection(this.currentExtraCollection);
    }
  }

  renderExtraDropdown(collections) {
    console.log('🎁 renderExtraDropdown called with collections:', collections);
    const dropdownText = document.getElementById('dropdownText');
    const dropdownOptions = document.getElementById('dropdownOptions');
    const dropdownSelected = document.getElementById('dropdownSelected');
    
    console.log('Dropdown elements found:', { dropdownText: !!dropdownText, dropdownOptions: !!dropdownOptions, dropdownSelected: !!dropdownSelected });
    
    if (!dropdownText || !dropdownOptions || !dropdownSelected) {
      console.error('❌ Dropdown elements not found');
      return;
    }
    
    // Clear existing options
    dropdownOptions.innerHTML = '';
    
    // Create options
    collections.forEach((collection, index) => {
      const option = document.createElement('div');
      option.className = 'dropdown-option';
      option.dataset.handle = collection.handle;
      option.textContent = collection.name || collection.handle.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      
      console.log(`Creating option ${index}:`, { name: option.textContent, handle: collection.handle });
      
      // Mark first option as selected
      if (index === 0) {
        option.classList.add('selected');
        dropdownText.textContent = option.textContent;
        console.log('Set first option as selected:', option.textContent);
      }
      
      // Add click handler
      option.addEventListener('click', () => {
        // Remove selected class from all options
        dropdownOptions.querySelectorAll('.dropdown-option').forEach(opt => opt.classList.remove('selected'));
        // Add selected class to clicked option
        option.classList.add('selected');
        // Update dropdown text
        dropdownText.textContent = option.textContent;
        // Close dropdown
        dropdownSelected.classList.remove('active');
        dropdownOptions.style.display = 'none';
        // Load collection
        const selectedCollection = collections.find(c => c.handle === option.dataset.handle);
        if (selectedCollection) {
          this.switchExtraCollection(selectedCollection);
        }
      });
      
      dropdownOptions.appendChild(option);
    });
    
    // Add click handler to dropdown selected.
    // Property assignment, NOT addEventListener: renderExtraDropdown runs on every
    // modal open, and dropdownSelected is static markup that persists for the life
    // of the page. addEventListener stacked a new toggle on each render, so an even
    // number of handlers opened then immediately closed the dropdown and it read as
    // dead. Assigning onclick replaces the previous handler instead.
    dropdownSelected.onclick = () => {
      const isActive = dropdownSelected.classList.contains('active');
      if (isActive) {
        dropdownSelected.classList.remove('active');
        dropdownOptions.style.display = 'none';
      } else {
        dropdownSelected.classList.add('active');
        dropdownOptions.style.display = 'block';
      }
    };
    
    // Close dropdown when clicking outside. Bound once for the life of the page:
    // this one is on document, which renderExtraDropdown cannot replace by assignment,
    // so without the guard it leaked a listener per render.
    if (!this._extraDropdownOutsideBound) {
      this._extraDropdownOutsideBound = true;
      document.addEventListener('click', (e) => {
        const sel = document.getElementById('dropdownSelected');
        const opts = document.getElementById('dropdownOptions');
        if (!sel || !opts) return;
        if (!sel.contains(e.target) && !opts.contains(e.target)) {
          sel.classList.remove('active');
          opts.style.display = 'none';
        }
      });
    }
  }

  switchExtraCollection(collection) {
    // Load new collection
    this.currentExtraCollection = collection;
    this.loadExtraCollection(collection);
  }

  async loadExtraCollection(collection) {
    console.log('🎁 loadExtraCollection called with:', collection);
    
    if (!collection) {
      console.error('❌ No collection provided to loadExtraCollection');
      return;
    }
    
    if (!collection.handle) {
      console.error('❌ Collection missing handle property:', collection);
      return;
    }
    
    const carouselContainer = document.getElementById('giftExtraCarousel');
    if (!carouselContainer) {
      console.error('❌ Gift extra carousel container not found');
      return;
    }
    
    carouselContainer.innerHTML = '<div style="text-align: center; padding: 20px;">Loading...</div>';
    
    try {
      // Fetch products from Shopify collection with full product data including images
      const response = await fetch(`/collections/${collection.handle}/products.json?limit=20`);
      if (!response.ok) throw new Error('Failed to fetch products');
      
      const data = await response.json();
      console.log('Full API response:', data);
      console.log('First product structure:', data.products[0]);
      this.renderExtraProducts(data.products);
      this.updateCarouselButtons();
      
      // Restore selected state after rendering
      this.restoreSelectedItems();
      
    } catch (error) {
      console.error('Error loading collection:', error);
      carouselContainer.innerHTML = '<p style="text-align: center; color: #666;">Failed to load products.</p>';
    }
  }
  
  restoreSelectedItems() {
    if (!this.selectedExtraItems || this.selectedExtraItems.length === 0) return;
    
    // Restore selected state for items that were previously selected
    this.selectedExtraItems.forEach(selectedItem => {
      const card = document.querySelector(`[data-variant-id="${selectedItem.variantId}"]`);
      if (card) {
        card.classList.add('selected');
      }
    });
  }

  renderExtraProducts(products) {
    const carouselContainer = document.getElementById('giftExtraCarousel');
    carouselContainer.innerHTML = '';
    
    console.log('Rendering products:', products);
    
    if (!products || products.length === 0) {
      carouselContainer.innerHTML = '<p style="text-align: center; color: #666;">No products found in this collection.</p>';
      return;
    }

    products.forEach(product => {
      const variant = product.variants[0]; // Use first variant
      const card = document.createElement('div');
      card.className = 'gift-extra-product-card';
      card.dataset.productId = product.id;
      card.dataset.variantId = variant.id;
      card.onclick = () => this.toggleExtraProduct(card, product, variant);
      
      // Shopify REST API returns prices as strings in decimal format (e.g., "69.00" for R 69)
      // NOT in cents. So we need to parse it as a float, not divide by 100
      let priceValue = 0;
      if (typeof variant.price === 'string') {
        priceValue = parseFloat(variant.price) || 0;
      } else if (typeof variant.price === 'number') {
        // If it's a number, it might be in cents (GraphQL) or decimal (REST)
        // Check if it's unreasonably large (> 10000) - likely in cents
        priceValue = variant.price > 10000 ? variant.price / 100 : variant.price;
      }
      
      const price = new Intl.NumberFormat('en-ZA', {
        style: 'currency',
        currency: 'ZAR',
        minimumFractionDigits: 0
      }).format(priceValue);
      
      // Try multiple ways to get the image (REST API structure)
      let imageUrl = '/assets/no-image.png';
      
      if (product.image && product.image.src) {
        // REST API featured image
        imageUrl = product.image.src;
      } else if (product.images && product.images.length > 0) {
        // REST API images array
        imageUrl = product.images[0].src;
      } else if (product.featured_image && product.featured_image.url) {
        // GraphQL API structure (fallback)
        imageUrl = product.featured_image.url;
      }
      
      console.log('Product:', product.title);
      console.log('Featured image object:', product.featured_image);
      console.log('Images array:', product.images);
      console.log('Image property:', product.image);
      console.log('Final image URL:', imageUrl);
      
      card.innerHTML = `
        <img src="${imageUrl}" alt="${product.title}" onerror="console.log('Image failed to load:', this.src); this.src='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDIwMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTIwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjEwMCIgeT0iNjAiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+Tm8gSW1hZ2U8L3RleHQ+Cjwvc3ZnPg=='">
        <div class="gift-extra-product-title">${product.title}</div>
        <div class="gift-extra-product-price">${price}</div>
      `;
      
      // Check if already selected
      const isSelected = this.selectedExtraItems && this.selectedExtraItems.some(item => item.variantId === variant.id);
      if (isSelected) {
        card.classList.add('selected');
      }
      
      carouselContainer.appendChild(card);
    });
  }

  toggleExtraProduct(card, product, variant) {
    console.log('Toggling extra product:', product.title);
    
    const isSelected = card.classList.contains('selected');
    
    if (isSelected) {
      // Remove from selection
      card.classList.remove('selected');
      this.selectedExtraItems = this.selectedExtraItems.filter(item => item.variantId !== variant.id);
    } else {
      // Add to selection
      card.classList.add('selected');
      
      // Get image URL
      let imageUrl = '/assets/no-image.png';
      if (product.image && product.image.src) {
        imageUrl = product.image.src;
      } else if (product.images && product.images.length > 0) {
        imageUrl = product.images[0].src;
      } else if (product.featured_image && product.featured_image.url) {
        imageUrl = product.featured_image.url;
      }
      
      // Store price - REST API returns as decimal string, convert to cents for consistency
      let priceInCents = 0;
      if (typeof variant.price === 'string') {
        priceInCents = Math.round(parseFloat(variant.price) * 100);
      } else if (typeof variant.price === 'number') {
        // If it's a number, check if it's already in cents (> 10000) or decimal
        priceInCents = variant.price > 10000 ? variant.price : Math.round(variant.price * 100);
      }
      
      this.selectedExtraItems.push({
        id: product.id,
        variantId: variant.id,
        title: product.title,
        price: priceInCents, // Store in cents for consistency
        imageUrl: imageUrl
      });
    }
    
    console.log('Selected extra items:', this.selectedExtraItems);
    
    // Update button text if in upsell_only mode
    if (this.mode === 'upsell_only') {
      this.updateStep2ButtonText();
    }
  }
  
  saveSelectedItemsAndClose() {
    // Collect selected items
    this.collectSelectedExtraItems();
    
    // Store globally for persistence
    window.selectedExtraItems = this.selectedExtraItems || [];
    
    // Update the "Add Something Extra" button
    this.updateUpsellButtonState();
    
    // Close modal
    this.close();
  }
  
  updateUpsellButtonState() {
    // Let the PDP recalc the add-to-cart button total (variant price + extras)
    document.dispatchEvent(new CustomEvent('blm:extras-changed'));

    const upsellButton = document.querySelector('.upsell-modal-button');
    if (!upsellButton) return;
    
    const upsellContent = upsellButton.querySelector('.upsell-content');
    if (!upsellContent) return;
    
    const selectedItems = window.selectedExtraItems || [];
    
    console.log('Updating upsell button state with items:', selectedItems);
    
    if (selectedItems.length === 0) {
      // Reset to default state
      upsellButton.classList.remove('has-selections');
      
      // Restore original content if stored
      if (upsellContent.dataset.originalContent) {
        upsellContent.innerHTML = upsellContent.dataset.originalContent;
      }
      
      // Toggle images
      const defaultImage = upsellButton.querySelector('.upsell-image-default');
      const selectedImage = upsellButton.querySelector('.upsell-image-selected');
      if (defaultImage) defaultImage.style.display = '';
      if (selectedImage) selectedImage.style.display = 'none';
      
      return;
    }
    
    // Store original content if not already stored
    if (!upsellContent.dataset.originalContent) {
      upsellContent.dataset.originalContent = upsellContent.innerHTML;
    }
    
    // Add selections class for styling
    upsellButton.classList.add('has-selections');
    
    // Get first item and additional count
    const mainItem = selectedItems[0];
    if (!mainItem) {
      console.error('No main item found in selectedItems');
      return;
    }
    
    const additionalCount = selectedItems.length - 1;
    
    // Get item title - handle various possible formats
    const itemTitle = mainItem.title || mainItem.name || 'Item';
    console.log('Item title:', itemTitle, 'from item:', mainItem);
    
    // Calculate total price - ensure price is a number
    const totalPrice = selectedItems.reduce((sum, item) => {
      const price = typeof item.price === 'number' ? item.price : parseInt(item.price) || 0;
      return sum + price;
    }, 0);
    
    console.log('Total price calculated:', totalPrice, 'from items:', selectedItems.map(i => ({ title: i.title, price: i.price })));
    
    // Format price - only format if price > 0
    let formattedPrice = 'R 0';
    if (totalPrice > 0) {
      formattedPrice = new Intl.NumberFormat('en-ZA', {
        style: 'currency',
        currency: 'ZAR',
        minimumFractionDigits: 0
      }).format(totalPrice / 100);
    }
    
    // Get selected state image if available
    const selectedImageElement = upsellButton.querySelector('.upsell-image-selected');
    let imageHtml = '';
    let imageSize = '50px';
    
    if (selectedImageElement) {
      // Get the image source - try multiple methods
      const imageSrc = selectedImageElement.src || selectedImageElement.getAttribute('src') || '';
      if (imageSrc) {
        // Get the size from the image element's style or default
        const computedStyle = window.getComputedStyle(selectedImageElement);
        imageSize = computedStyle.width || selectedImageElement.style.width || '50px';
        imageHtml = `<img src="${imageSrc}" alt="Add Something Extra" style="width: ${imageSize}; height: ${imageSize}; object-fit: contain;" class="selected-state-icon">`;
      }
    }
    
    // If no selected image found, use product image as fallback
    if (!imageHtml) {
      const fallbackImage = mainItem.imageUrl || mainItem.image || '/assets/no-image.png';
      imageHtml = `<img src="${fallbackImage}" alt="${itemTitle}" style="width: 50px; height: 50px; object-fit: cover; border-radius: 4px;" class="selected-state-icon">`;
    }
    
    // Create selected state HTML with white text (no checkmark div - CSS ::after handles it)
    const selectedHTML = `
      <div class="upsell-selected-summary" style="display: flex; align-items: center; gap: 12px; width: 100%;">
        <div class="selected-item-thumb" style="width: 50px; height: 50px; border-radius: 4px; object-fit: cover; flex-shrink: 0; display: flex; align-items: center; justify-content: center;">
          ${imageHtml}
        </div>
        <div class="selected-item-details" style="flex: 1; min-width: 0;">
          <div class="selected-item-title" style="font-weight: 600; font-size: 14px; color: #ffffff; margin-bottom: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${itemTitle}</div>
          ${additionalCount > 0 ? `<div class="additional-count" style="font-size: 12px; color: rgba(255, 255, 255, 0.9); margin-bottom: 2px;">(+${additionalCount} More)</div>` : ''}
          <div class="selected-total-price" style="font-size: 12px; color: rgba(255, 255, 255, 0.9);">${formattedPrice}</div>
        </div>
      </div>
    `;
    
    upsellContent.innerHTML = selectedHTML;
    
    // Hide default images
    const defaultImage = upsellButton.querySelector('.upsell-image-default');
    if (defaultImage) defaultImage.style.display = 'none';
    if (selectedImageElement) selectedImageElement.style.display = 'none'; // Hide the original selected image since we're using it in the HTML
  }

  updateCarouselButtons() {
    const carousel = document.getElementById('giftExtraCarousel');
    const prevBtn = document.getElementById('giftCarouselPrev');
    const nextBtn = document.getElementById('giftCarouselNext');
    
    if (!carousel || !prevBtn || !nextBtn) return;
    
    // Show buttons if content overflows
    const hasOverflow = carousel.scrollWidth > carousel.clientWidth;
    prevBtn.style.display = hasOverflow ? 'block' : 'none';
    nextBtn.style.display = hasOverflow ? 'block' : 'none';
  }

  async skip() {
    try {
      console.log('Skip gift message clicked');
      console.log('Mode:', this.mode);
      console.log('Cart item key:', this.currentCartItemKey);
      
      // If we're updating a cart item (cart context), just close the modal
      if (this.currentCartItemKey) {
        console.log('In cart context, just closing modal without changes');
        this.close();
        return;
      }
      
      // If PDP has a file upload selected, submit the native product form so the file is included
      const productForm = this.getProductForm();
      if (this.hasFileSelected(productForm)) {
        console.log('File selected – using multipart add for main product (skip flow)');
        await this.addMainProductWithFormData(productForm, {} /* no extra props */, true);
        this.close();
        return;
      }
      
      // Otherwise, we're on PDP - add item to cart without gift message
      await this.addAllItemsToCart(true); // Open cart drawer
      this.close();
    } catch (error) {
      console.error('Error skipping gift message:', error);
      alert('There was an error adding the item to your cart. Please try again.');
    }
  }

  async continueShoppingFromStep1() {
    try {
      console.log('Continue shopping from step 1 clicked');
      await this.addAllItemsToCart(false); // Don't open cart drawer
      this.showSuccessAnimation();
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('There was an error adding the item to your cart. Please try again.');
    }
  }

  async continueShoppingFromStep2() {
    try {
      console.log('Continue shopping from step 2 clicked');
      await this.addAllItemsToCart(false); // Don't open cart drawer
      this.showSuccessAnimation();
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('There was an error adding the item to your cart. Please try again.');
    }
  }

  async submitGiftMessage() {
    try {
      console.log('Submit gift message clicked, mode:', this.mode);
      console.log('Current cart item key:', this.currentCartItemKey);
      
      // Check if we're updating an existing cart item
      if (this.currentCartItemKey) {
        // Update existing cart item (no validation - allow empty to remove message)
        await this.updateCartItemGiftMessage();
        this.close();
        // Reload the page to show updated cart
        window.location.reload();
        return;
      }
      
      // PDP path: if a file is selected, submit native form with gift properties
      const productForm = this.getProductForm();
      if (this.hasFileSelected(productForm)) {
        console.log('File selected – using multipart add with gift properties');
        const giftProps = this.getFormProperties();
        await this.addMainProductWithFormData(productForm, giftProps, true);
        this.close();
        return;
      }
      
      // No validation - gift messages are optional
      // Customers can add products with or without messages
      
      await this.addAllItemsToCart(true); // Open cart drawer
      this.showSuccessAnimation();
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.announceToScreenReader('There was an error adding the item to your cart. Please try again.');
      alert('There was an error adding the item to your cart. Please try again.');
    }
  }
  
  // Helper: get the main product form element
  getProductForm() {
    const candidate =
      document.querySelector('.js-product-form, .js-product-form-main') ||
      document.querySelector('product-form form') ||
      document.querySelector('form[action*="/cart/add"]');
    if (!candidate) return null;
    return candidate.tagName === 'FORM' ? candidate : candidate.querySelector && candidate.querySelector('form');
  }
  
  // Helper: check if any file input associated with the form has a selected file
  hasFileSelected(form) {
    if (!form) return false;
    const formId = form.getAttribute('id');
    const cssEscape = (window.CSS && typeof window.CSS.escape === 'function')
      ? window.CSS.escape
      : (s) => (s || '').replace(/[^a-zA-Z0-9_-]/g, '\\$&');
    const selector = formId
      ? `#${cssEscape(formId)} input[type="file"][name^="properties["], input[type="file"][form="${cssEscape(formId)}"][name^="properties["]`
      : `input[type="file"][name^="properties["]`;
    const inputs = document.querySelectorAll(selector);
    for (const input of inputs) {
      if (input.files && input.files.length > 0) return true;
    }
    return false;
  }
  
  // Helper: add main product with FormData (supports file uploads), then add extras (upsells/greeting)
  async addMainProductWithFormData(form, extraProps = {}, openCartDrawer = true) {
    if (!form) throw new Error('Product form not found');
    // Exclusive product types ship on their own: clear an incompatible cart
    // first (the PDP warns before this point). Defined in main-product.liquid.
    if (typeof window.blmEnsureCartCompatible === 'function') {
      await window.blmEnsureCartCompatible();
    }
    // Build FormData from the form
    const fd = new FormData(form);
    // Ensure variant id present
    try {
      const variantId = this.getCurrentVariantId();
      if (variantId && !fd.get('id')) {
        fd.set('id', variantId);
      }
    } catch (e) {
      console.warn('Could not determine variant id for FormData add:', e);
    }
    // Append gift properties only (do not duplicate personalization; those are already in the form)
    Object.keys(extraProps || {}).forEach((key) => {
      if (key === 'Enter a gift message' || key === 'Gift Occasion') {
        fd.append(`properties[${key}]`, extraProps[key]);
      }
    });
    // Submit multipart request to add main product (includes files)
    const addResp = await fetch('/cart/add.js', { method: 'POST', body: fd });
    if (!addResp.ok) {
      const err = await addResp.text();
      throw new Error(`Add main product failed: ${err}`);
    }
    // Build additional items: greeting card (if selected) + upsells + extra items
    const additionalItems = this.buildAdditionalItemsFromSelections();
    if (additionalItems.length > 0) {
      const upsellResp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: additionalItems })
      });
      if (!upsellResp.ok) {
        console.warn('Adding additional items failed');
      }
      // Clear selections state
      if (this.selectedExtraItems && this.selectedExtraItems.length > 0) {
        this.selectedExtraItems = [];
      }
      if (window.selectedUpsells && window.selectedUpsells.length > 0) {
        window.selectedUpsells = [];
      }
    }
    // Update cart UI
    if (openCartDrawer) {
      await this.updateCartUI();
    } else {
      await this.updateCartCountOnly();
    }
  }
  
  // Helper: collect additional items after main add (greeting card + upsells + extra items)
  buildAdditionalItemsFromSelections() {
    const items = [];
    // Greeting card flow
    if (this.isGreetingCardFlow && this.selectedCardVariantId) {
      const greetingCardMessage = this.greetingCardMessage ? this.greetingCardMessage.value.trim() : '';
      items.push({
        id: this.selectedCardVariantId,
        quantity: 1,
        // 'Enter a gift message', NOT a card-specific key. The fulfilment
        // dashboard reads this one property, so a premium card message stored
        // under its own name never reached the florist (order BLM100231).
        properties: { 'Enter a gift message': greetingCardMessage }
      });
    }
    // Upsell modal selections (legacy/global)
    if (window.selectedUpsells && window.selectedUpsells.length > 0) {
      window.selectedUpsells.forEach(upsell => {
        items.push({
          id: upsell.variantId,
          quantity: upsell.quantity || 1
        });
      });
    }
    // Step 2 extra items (this.selectedExtraItems)
    if (this.selectedExtraItems && this.selectedExtraItems.length > 0) {
      this.selectedExtraItems.forEach(extraItem => {
        items.push({
          id: extraItem.variantId,
          quantity: extraItem.quantity || 1
        });
      });
    }
    return items;
  }
  
  async updateCartItemGiftMessage() {
    try {
      // Merge gift fields into existing line item properties instead of overwriting
      // 1) Load current cart and find this item by key
      let existingProperties = {};
      try {
        const cartRes = await fetch('/cart.js', { method: 'GET' });
        if (cartRes.ok) {
          const cart = await cartRes.json();
          const item = (cart.items || []).find(i => i.key === this.currentCartItemKey);
          if (item && item.properties) {
            // Clone existing properties so we don't drop personalisation fields
            existingProperties = Object.assign({}, item.properties);
          }
        }
      } catch (e) {
        console.warn('Could not load existing cart item for merge; proceeding with gift fields only', e);
      }
      
      // 2) Prepare gift fields (empty string removes the property)
      const properties = Object.assign({}, existingProperties);
      
      // Free gift messages and premium card messages share one line-item
      // property. They never apply to the same line — a free message sits on the
      // product line, a card message on the card line — so whichever field the
      // shopper filled in is the message for this line. The free textarea takes
      // precedence, since that is the one bound to the line being edited; the
      // card textarea is fetched by id and could hold text from another context.
      const cardTextarea = this.cardMessageTextarea || document.getElementById('greetingCardMessage');
      const cardMessage = cardTextarea ? cardTextarea.value.trim() : '';
      const freeMessage = this.messageTextarea ? this.messageTextarea.value.trim() : '';

      // Empty string removes the property.
      properties['Enter a gift message'] = freeMessage || cardMessage;

      // Retire the old key on any line still carrying it, so a message cannot
      // linger under a name the fulfilment dashboard does not read.
      if (Object.prototype.hasOwnProperty.call(properties, 'Greeting Card Message')) {
        properties['Greeting Card Message'] = '';
      }

      if (this.occasionSelect && this.occasionSelect.value) {
        // Format occasion name (e.g., "mothers_day" -> "Mother's Day")
        const formattedOccasion = this.occasionSelect.value
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        properties['Gift Occasion'] = formattedOccasion;
      } else {
        // Set to empty string to remove the property
        properties['Gift Occasion'] = '';
      }
      
      console.log('Updating cart item gift message:', {
        key: this.currentCartItemKey,
        properties: properties
      });
      
      // Update the cart item with new properties
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: this.currentCartItemKey,
          properties: properties
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update cart item');
      }
      
      const data = await response.json();
      console.log('Cart item updated successfully:', data);
      
      this.announceToScreenReader('Gift message updated successfully');
      
      // Clear the cart item tracking
      this.currentCartItemKey = null;
      this.currentCartItemId = null;
      
    } catch (error) {
      console.error('Error updating cart item:', error);
      throw error;
    }
  }

  getFormProperties() {
    const properties = {};
    
    // 1) Gift message fields (existing behavior)
    if (this.messageTextarea && this.messageTextarea.value.trim()) {
      properties['Enter a gift message'] = this.messageTextarea.value.trim();
    }
    
    if (this.occasionSelect && this.occasionSelect.value) {
      // Format occasion name (e.g., "mothers_day" -> "Mother's Day")
      const formattedOccasion = this.occasionSelect.value
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      properties['Gift Occasion'] = formattedOccasion;
    }
    
    // 2) Personalization fields from product form (`properties[...]`)
    try {
      const productForm = document.querySelector('.js-product-form, .js-product-form-main, product-form form');
      const formId = productForm ? productForm.getAttribute('id') : null;
      const cssEscape = (window.CSS && typeof window.CSS.escape === 'function')
        ? window.CSS.escape
        : (s) => (s || '').replace(/[^a-zA-Z0-9_-]/g, '\\$&');
      
      // Collect inputs that are inside the form OR associated via form attribute
      const selector = formId
        ? `#${cssEscape(formId)} [name^="properties["], [form="${cssEscape(formId)}"][name^="properties["]`
        : `[name^="properties["]`;
      
      const nodes = document.querySelectorAll(selector);
      nodes.forEach((el) => {
        // Skip gift message keys handled above
        const name = el.getAttribute('name');
        if (!name || !name.startsWith('properties[')) return;
        if (name === 'properties[Enter a gift message]' || name === 'properties[Gift Occasion]') return;
        
        // Hidden/system keys start with underscore
        const key = name.substring('properties['.length, name.length - 1);
        if (!key || key.charAt(0) === '_') return;
        
        // Handle element types
        let value = '';
        if (el.type === 'checkbox') {
          if (!el.checked) return;
          value = el.value || 'Yes';
        } else if (el.type === 'file') {
          // Files cannot be sent via JSON /cart/add.js.
          // If a file is present, leave it out so we don't break add-to-cart.
          // Native form submission will handle file uploads.
          return;
        } else {
          value = (el.value || '').trim();
          if (value === '') return;
        }
        
        properties[key] = value;
      });
    } catch (e) {
      console.warn('Could not collect personalization properties:', e);
    }
    
    return properties;
  }

  getCurrentVariantId() {
    // Try multiple methods to get the current variant ID
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('variant')) {
      return urlParams.get('variant');
    }
    
    // Try to get from form
    const variantInput = document.querySelector('input[name="id"], input[name="variant_id"]');
    if (variantInput) {
      return variantInput.value;
    }
    
    // Fallback to product's default variant (if available globally)
    if (window.productVariants && window.productVariants.length > 0) {
      return window.productVariants[0].id;
    }
    
    // Final fallback - try to get from meta tag or data attribute
    const productForm = document.querySelector('.product-form, [data-product-form]');
    if (productForm) {
      const variantId = productForm.dataset.variantId || productForm.dataset.currentVariant;
      if (variantId) return variantId;
    }
    
    throw new Error('Could not determine variant ID');
  }

  async addToCart(variantId, properties = {}) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: properties
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to add to cart');
    }

    return response.json();
  }

  async addAllItemsToCart(openCartDrawer = true) {
    try {
      // Exclusive product types ship on their own: clear an incompatible cart
      // first (the PDP warns before this point). Defined in main-product.liquid.
      if (typeof window.blmEnsureCartCompatible === 'function') {
        await window.blmEnsureCartCompatible();
      }

      const items = [];

      // The main product ALWAYS goes in the cart. A greeting card is an extra
      // line item alongside it, never a replacement — the greeting-card branch
      // used to push only the card, so choosing a premium card added the card
      // and silently dropped the flowers it was meant to accompany.
      const properties = this.getFormProperties();
      const mainVariantId = this.getCurrentVariantId();
      if (mainVariantId) {
        items.push({
          id: mainVariantId,
          quantity: 1,
          properties: properties
        });
      }

      if (this.isGreetingCardFlow && this.selectedCardVariantId) {
        // The message belongs on the card line, not the product line.
        const greetingCardMessage = this.greetingCardMessage ? this.greetingCardMessage.value.trim() : '';
        items.push({
          id: this.selectedCardVariantId,
          quantity: 1,
          properties: {
            // Same canonical key as the free gift message — see note above.
            'Enter a gift message': greetingCardMessage
          }
        });
      }

      // Add upsell items (from upsell modal selection)
      if (window.selectedUpsells && window.selectedUpsells.length > 0) {
        console.log('Adding upsell items:', window.selectedUpsells);
        window.selectedUpsells.forEach(upsell => {
          items.push({
            id: upsell.variantId,
            quantity: upsell.quantity || 1
          });
        });
      }

      // Selected extras live in TWO places: this.selectedExtraItems (chosen in
      // step 2) and window.selectedExtraItems (the global the upsell_only flow
      // writes). saveSelectedItemsAndClose() copies one into the other, so
      // iterating both — as this used to — added every extra to the cart TWICE.
      // Merge the two and de-duplicate by variant.
      const extraCandidates = []
        .concat(Array.isArray(this.selectedExtraItems) ? this.selectedExtraItems : [])
        .concat(Array.isArray(window.selectedExtraItems) ? window.selectedExtraItems : []);

      if (extraCandidates.length > 0) {
        const seenExtraVariants = new Set();
        const dedupedExtras = [];
        extraCandidates.forEach(extraItem => {
          if (!extraItem || !extraItem.variantId) return;
          const key = extraItem.variantId.toString();
          if (seenExtraVariants.has(key)) return;
          seenExtraVariants.add(key);
          dedupedExtras.push(extraItem);
          items.push({
            id: extraItem.variantId,
            quantity: 1
          });
        });
        console.log('Adding extra items (deduped):', dedupedExtras);
      }

      if (items.length === 0) {
        throw new Error('No items to add to cart');
      }

      console.log('Adding items to cart:', items);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add items to cart');
      }

      // Clear selected items after successful add
      if (window.selectedUpsells && window.selectedUpsells.length > 0) {
        window.selectedUpsells = [];
        // Update the upsell button to show default state
        if (window.upsellModal) {
          window.upsellModal.selectedItems = [];
          window.upsellModal.updateUpsellButton();
        }
      }

      if (this.selectedExtraItems && this.selectedExtraItems.length > 0) {
        this.selectedExtraItems = [];
      }

      // Update cart UI
      if (openCartDrawer) {
        this.updateCartUI();
      } else {
        this.updateCartCountOnly();
      }

      return response.json();
    } catch (error) {
      console.error('Error adding all items to cart:', error);
      throw error;
    }
  }

  hasUpsellItemsSelected() {
    // Check all possible sources of selected upsell items:
    // 1. window.selectedUpsells - from standalone upsell modal
    // 2. this.selectedExtraItems - from gift message modal step 2
    // 3. window.selectedExtraItems - global storage from upsell_only mode
    const hasUpsellModalItems = window.selectedUpsells && window.selectedUpsells.length > 0;
    const hasExtraItems = this.selectedExtraItems && this.selectedExtraItems.length > 0;
    const hasGlobalExtraItems = window.selectedExtraItems && window.selectedExtraItems.length > 0;
    
    console.log('Checking for upsell items:', { hasUpsellModalItems, hasExtraItems, hasGlobalExtraItems });
    
    return hasUpsellModalItems || hasExtraItems || hasGlobalExtraItems;
  }

  collectSelectedExtraItems() {
    // The carousel only ever holds the category currently chosen in the
    // dropdown — switching category re-renders it. So the DOM is NOT the source
    // of truth for what the customer picked: this.selectedExtraItems is, kept up
    // to date by toggleExtraProduct across every category.
    //
    // Rebuilding the array from the DOM (as this used to) silently dropped every
    // add-on chosen in a category the customer had navigated away from. Picking
    // two chocolates worked because both cards were still on screen; picking a
    // chocolate and then a balloon did not.
    //
    // Reconcile instead: only variants that are actually VISIBLE may be added or
    // removed here. Anything selected in another category is left alone.
    const step2 = document.getElementById('giftStep2');
    const visibleCards = step2 ? step2.querySelectorAll('.gift-extra-product-card') : [];

    const byVariant = new Map();
    if (Array.isArray(this.selectedExtraItems)) {
      this.selectedExtraItems.forEach(item => {
        if (item && item.variantId) byVariant.set(item.variantId.toString(), item);
      });
    }

    visibleCards.forEach(card => {
      const variantId = card.getAttribute('data-variant-id');
      if (!variantId) return;
      const key = variantId.toString();
      const existingItem = byVariant.get(key);

      if (!card.classList.contains('selected')) {
        // Visible and deselected: the customer turned this one off.
        byVariant.delete(key);
        return;
      }

      if (existingItem && existingItem.title && existingItem.price) {
        // Already captured with full data.
        return;
      }

      {
        // Selected but not yet captured: read it off the card.
        const productId = card.getAttribute('data-product-id');
        const quantity = parseInt(card.getAttribute('data-quantity')) || 1;

        // Get product data from card
        const titleElement = card.querySelector('.gift-extra-product-title');
        const priceElement = card.querySelector('.gift-extra-product-price');
        const imageElement = card.querySelector('img');
        
        const title = titleElement ? titleElement.textContent.trim() : '';
        const priceText = priceElement ? priceElement.textContent.trim() : '';
        // Extract price from formatted text (e.g., "R 0.69" -> 69 cents)
        // Shopify REST API returns prices in cents, so "R 0.69" means variant.price = 69
        // When extracting from DOM, we need to parse the decimal and convert to cents
        let price = 0;
        if (priceText) {
          // Remove currency symbol but keep decimal point, then parse as decimal (e.g., "R 0.69" -> "0.69" -> 0.69)
          const priceMatch = priceText.replace(/[^\d.]/g, '').replace(/\.(?=.*\.)/g, ''); // Remove all but last decimal point
          if (priceMatch) {
            const priceInCurrency = parseFloat(priceMatch) || 0;
            price = Math.round(priceInCurrency * 100); // Convert to cents (0.69 -> 69)
          }
        }
        const imageUrl = imageElement ? imageElement.src : '/assets/no-image.png';
        
        byVariant.set(key, {
          id: productId,
          variantId: variantId,
          title: title,
          price: price,
          imageUrl: imageUrl,
          quantity: quantity
        });
      }
    });

    this.selectedExtraItems = Array.from(byVariant.values());
    console.log('Collected extra items:', this.selectedExtraItems);
  }

  /* Re-render the header cart badge from the server, the same way
     sections/hero-search.liquid does. Returns the full styled markup, so the
     badge keeps its shape and colour instead of collapsing to a plain number.
     The `shopify-section` wrapper this injects is neutralised by the
     `display: contents` rule in sections/header.liquid. */
  async refreshCartIconBubble() {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    try {
      const res = await fetch('/?sections=cart-icon-bubble');
      const sections = await res.json();
      if (sections && sections['cart-icon-bubble'] != null) {
        bubble.innerHTML = sections['cart-icon-bubble'];
      }
    } catch (e) {
      // Leave the existing badge alone rather than replacing it with junk.
      console.warn('[gift-message-modal] cart bubble refresh failed:', e);
    }
  }

  async updateCartUI() {
    try {
      // Update cart count
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      // Update cart count elements.
      // Deliberately NOT #cart-icon-bubble: that is the CONTAINER for the
      // styled badge, so setting textContent on it destroys the
      // .header__cart-count element and leaves a bare number sitting under the
      // bag icon until the next full page load. It is re-rendered from the
      // server instead, just below.
      const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, .cart-link__bubble');
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count;
        if (cart.item_count > 0) {
          element.classList.add('cart-link__bubble--visible');
        }
      });

      await this.refreshCartIconBubble();

      // Refresh cart drawer content first
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));

      // Open cart drawer using the theme's standard method
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
          detail: {
            opener: document.querySelector('.product-info__add-button button[name="add"]')
          }
        }));
      }, 100);

      // Dispatch custom event for other scripts
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: cart }
      }));

    } catch (error) {
      console.error('Error updating cart UI:', error);
    }
  }

  async updateCartCountOnly() {
    try {
      // Update cart count without opening drawer
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      // Update cart count elements.
      // Deliberately NOT #cart-icon-bubble: that is the CONTAINER for the
      // styled badge, so setting textContent on it destroys the
      // .header__cart-count element and leaves a bare number sitting under the
      // bag icon until the next full page load. It is re-rendered from the
      // server instead, just below.
      const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, .cart-link__bubble');
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count;
        if (cart.item_count > 0) {
          element.classList.add('cart-link__bubble--visible');
        }
      });

      await this.refreshCartIconBubble();

      // Refresh cart drawer content but don't open it
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));

      // Dispatch custom event for other scripts
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: cart }
      }));

    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }
};

// Global functions for backward compatibility and easy access
function showGiftMessage() {
  console.log('showGiftMessage called, window.giftMessageModal:', window.giftMessageModal);
  if (window.giftMessageModal && typeof window.giftMessageModal.show === 'function') {
    window.giftMessageModal.show({ mode: 'gift_upsell' });
  } else {
    console.error('Gift message modal not available or show method not found');
  }
}

function closeGiftMessage() {
  if (window.giftMessageModal) {
    window.giftMessageModal.close();
  }
}

function showGiftStep1() {
  if (window.giftMessageModal) {
    window.giftMessageModal.showStep1();
  }
}

function showGiftStep2() {
  if (window.giftMessageModal) {
    window.giftMessageModal.show({ mode: 'upsell_only' });
  }
}

// New helper functions for the three modes
function openGiftFlow() {
  if (window.giftMessageModal) {
    window.giftMessageModal.show({ mode: 'gift_upsell' });
  }
}

function openUpsellOnlyFlow() {
  if (window.giftMessageModal) {
    window.giftMessageModal.show({ mode: 'upsell_only' });
  }
}

function openGiftOnlyFlow() {
  if (window.giftMessageModal) {
    window.giftMessageModal.show({ mode: 'gift_only' });
  }
}

// Function to open gift message modal from cart
// Used by "Add Gift Message" and "Edit Gift Message" buttons in cart
function openGiftMessageModal(itemKey, itemId, existingMessage, existingGreetingCardMessage, existingOccasion) {
  console.log('🎁 openGiftMessageModal called');
  console.log('Parameters:', { itemKey, itemId, existingMessage, existingGreetingCardMessage, existingOccasion });
  console.log('Modal exists:', !!window.giftMessageModal);
  console.log('Modal object:', window.giftMessageModal);
  
  if (!window.giftMessageModal) {
    console.error('❌ Gift message modal not initialized');
    return;
  }
  
  console.log('✅ Modal found, proceeding to open...');
  
  // Store cart item information for updating
  window.giftMessageModal.currentCartItemKey = itemKey;
  window.giftMessageModal.currentCartItemId = itemId;
  
  console.log('Set cart item key:', window.giftMessageModal.currentCartItemKey);
  
  // Clear any existing form values first
  const messageTextarea = document.getElementById('giftMessage');
  const greetingCardTextarea = document.getElementById('greetingCardMessage');
  const occasionSelect = document.getElementById('occasion');
  
  if (messageTextarea) {
    messageTextarea.value = '';
  }
  if (greetingCardTextarea) {
    greetingCardTextarea.value = '';
  }
  if (occasionSelect) {
    occasionSelect.value = '';
  }
  
  // Determine if we're editing an existing message and what type
  const hasExistingMessage = (existingMessage && existingMessage !== '') || (existingGreetingCardMessage && existingGreetingCardMessage !== '');
  const isGreetingCardMessage = existingGreetingCardMessage && existingGreetingCardMessage !== '';
  
  console.log('Has existing message:', hasExistingMessage);
  console.log('Is greeting card message:', isGreetingCardMessage);
  
  if (hasExistingMessage) {
    // Pre-fill the appropriate form based on message type
    if (isGreetingCardMessage) {
      console.log('Pre-filling greeting card message:', existingGreetingCardMessage);
      if (greetingCardTextarea) {
        greetingCardTextarea.value = existingGreetingCardMessage;
        // Update character count
        if (window.giftMessageModal.updateCardCharCount) {
          window.giftMessageModal.updateCardCharCount();
        }
      }
    } else {
      console.log('Pre-filling free note message:', existingMessage);
      if (messageTextarea) {
        messageTextarea.value = existingMessage;
        // Update character count
        if (window.giftMessageModal.updateCharCount) {
          window.giftMessageModal.updateCharCount();
        }
      }
    }
    
    if (existingOccasion && existingOccasion !== '') {
      console.log('Pre-filling existing occasion:', existingOccasion);
      if (occasionSelect) {
        occasionSelect.value = existingOccasion;
      }
    }
  }
  
  // Determine the appropriate mode and step
  if (hasExistingMessage) {
    // Editing existing message - go directly to the appropriate step
    if (isGreetingCardMessage) {
      console.log('🎯 Editing greeting card message - going to Step 1b');
      window.giftMessageModal.mode = 'gift_only';
      window.giftMessageModal.show({ mode: 'gift_only' });
      // Navigate to Step 1b after modal opens
      setTimeout(() => {
        window.giftMessageModal.showStep1b();
      }, 100);
    } else {
      console.log('🎯 Editing free note message - going to Step 1a');
      window.giftMessageModal.mode = 'gift_only';
      window.giftMessageModal.show({ mode: 'gift_only' });
      // Navigate to Step 1a after modal opens
      setTimeout(() => {
        window.giftMessageModal.showStep1a();
      }, 100);
    }
  } else {
    // Adding new message - go to Step 0 for choice selection (but skip upsell)
    console.log('🎯 Adding new message - going to Step 0 (choice selection)');
    window.giftMessageModal.mode = 'gift_only';
    window.giftMessageModal.show({ mode: 'gift_only' });
    // Navigate to Step 0 after modal opens
    setTimeout(() => {
      window.giftMessageModal.showStep0();
    }, 100);
  }
  
  console.log('✅ Modal.show() completed');
}

/**
 * Adds a gift message to a cart line that doesn't have one yet.
 *
 * Goes straight to the free-note step rather than the card/note choice:
 * the cart edit path only rewrites line item properties, so it cannot add
 * the paid greeting card product as its own line. Customers who want a
 * premium card add it from the product page.
 */
function openGiftMessageAddFromCart(itemKey, itemId) {
  if (!window.giftMessageModal) {
    console.error('❌ Gift message modal not initialized');
    return;
  }

  window.giftMessageModal.currentCartItemKey = itemKey;
  window.giftMessageModal.currentCartItemId = itemId;

  const messageTextarea = document.getElementById('giftMessage');
  const greetingCardTextarea = document.getElementById('greetingCardMessage');
  const occasionSelect = document.getElementById('occasion');
  if (messageTextarea) messageTextarea.value = '';
  if (greetingCardTextarea) greetingCardTextarea.value = '';
  if (occasionSelect) occasionSelect.value = '';

  window.giftMessageModal.mode = 'gift_only';
  window.giftMessageModal.show({ mode: 'gift_only' });
  setTimeout(() => {
    window.giftMessageModal.showStep1a();
  }, 100);
}

function skipGiftMessage() {
  if (window.giftMessageModal) {
    window.giftMessageModal.skip();
  }
}

function continueShoppingFromStep1() {
  if (window.giftMessageModal) {
    window.giftMessageModal.continueShoppingFromStep1();
  }
}

function continueShoppingFromStep2() {
  if (window.giftMessageModal) {
    window.giftMessageModal.continueShoppingFromStep2();
  }
}

function submitGiftMessage() {
  if (window.giftMessageModal) {
    window.giftMessageModal.submitGiftMessage();
  }
}

function proceedToExtraItems() {
  console.log('⏭️ proceedToExtraItems called');
  console.trace('Called from:');
  
  if (window.giftMessageModal) {
    console.log('Current mode:', window.giftMessageModal.mode);
    console.log('Has cart item key:', !!window.giftMessageModal.currentCartItemKey);
    
    // If we're in gift_only mode (cart context), just submit directly
    if (window.giftMessageModal.mode === 'gift_only') {
      console.log('✅ In gift_only mode, submitting directly');
      window.giftMessageModal.submitGiftMessage();
      return;
    }
    
    // Check if upsell items are already selected (from "Add Something Extra" button)
    if (window.giftMessageModal.hasUpsellItemsSelected()) {
      // Skip step 2 and go directly to cart since user already selected upsell items
      console.log('✅ Upsell items already selected via "Add Something Extra" button, skipping step 2');
      console.log('Selected items:', {
        selectedUpsells: window.selectedUpsells,
        selectedExtraItems: window.selectedExtraItems,
        modalExtraItems: window.giftMessageModal.selectedExtraItems
      });
      window.giftMessageModal.submitGiftMessage();
    } else {
      // Check if upsell collections are configured
      const upsellCollections = window.upsellModalCollections || null;
      if (!upsellCollections || upsellCollections.length === 0) {
        // No upsell collections configured, skip step 2
        console.log('No upsell collections configured, proceeding directly to cart');
        window.giftMessageModal.submitGiftMessage();
      } else {
        // Show step 2 for extra items
        console.log('Upsell collections configured, showing step 2');
        window.giftMessageModal.showStep2();
      }
    }
  }
}

function scrollGiftCarousel(direction) {
  const carousel = document.getElementById('giftExtraCarousel');
  if (!carousel) return;
  
  const cardWidth = 200 + 15; // card width + gap
  const scrollAmount = cardWidth * 2; // scroll 2 cards at a time
  
  if (direction === 'prev') {
    carousel.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
  } else {
    carousel.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  }
}

function handleStep2Add() {
  if (window.giftMessageModal) {
    if (window.giftMessageModal.mode === 'upsell_only') {
      // In upsell_only mode, just save selections and close modal
      window.giftMessageModal.saveSelectedItemsAndClose();
    } else {
      // In other modes, add to cart
      submitWithExtraItems();
    }
  }
}

function submitWithExtraItems() {
  if (window.giftMessageModal) {
    // Add any selected extra items from step 2
    window.giftMessageModal.collectSelectedExtraItems();
    window.giftMessageModal.submitGiftMessage();
  }
}

function skipExtraItems() {
  if (window.giftMessageModal) {
    if (window.giftMessageModal.mode === 'upsell_only') {
      // In upsell_only mode, just close without saving
      window.giftMessageModal.close();
    } else {
      // In other modes, proceed to cart
      window.giftMessageModal.submitGiftMessage();
    }
  }
}

function showGiftStep3() {
  if (window.giftMessageModal) {
    window.giftMessageModal.showStep2(); // Redirect to step 2 since step 3 no longer exists
  }
}

// Step 0 navigation functions
function selectFreeNote() {
  console.log('🎁 selectFreeNote called');
  if (window.giftMessageModal) {
    window.giftMessageModal.isGreetingCardFlow = false;
    hideStep('giftStep0');
    showStep('giftStep1a');
  }
}

function selectPremiumCard() {
  console.log('🎁 selectPremiumCard called');
  if (window.giftMessageModal) {
    window.giftMessageModal.isGreetingCardFlow = true;
    hideStep('giftStep0');
    showStep('giftStep1b');
    resetCardSubSteps();
    loadGreetingCardDesigns();
  }
}

function backToStep0() {
  console.log('🎁 backToStep0 called');
  hideStep('giftStep1a');
  hideStep('giftStep1b');
  showStep('giftStep0');
  if (window.giftMessageModal) {
    window.giftMessageModal.isGreetingCardFlow = false;
  }
}

// Greeting card specific functions
function updateCardVariants() {
  console.log('🎁 updateCardVariants called');
  const languageRadio = document.querySelector('input[name="cardLanguage"]:checked');
  console.log('📻 Selected radio:', languageRadio);
  
  if (languageRadio && window.giftMessageModal) {
    const selectedLanguage = languageRadio.value;
    console.log('🌍 Setting language to:', selectedLanguage);
    window.giftMessageModal.cardLanguage = selectedLanguage;
    
    // Update visual selected state for radio buttons
    document.querySelectorAll('.radio-option').forEach(option => {
      option.classList.remove('selected');
    });
    if (languageRadio.closest('.radio-option')) {
      languageRadio.closest('.radio-option').classList.add('selected');
    }
    
    console.log('🔄 Calling loadGreetingCardDesigns...');
    loadGreetingCardDesigns();
  } else {
    console.error('❌ Radio button or modal not found');
  }
}

function scrollCardCarousel(direction) {
  console.log('🎁 scrollCardCarousel called:', direction);
  const carousel = document.getElementById('cardDesignCarousel');
  if (carousel) {
    const scrollAmount = 220; // Width of card + gap
    if (direction === 'prev') {
      carousel.scrollLeft -= scrollAmount;
    } else {
      carousel.scrollLeft += scrollAmount;
    }
  }
}

async function loadGreetingCardDesigns() {
  console.log('🎁 loadGreetingCardDesigns called');
  console.log('window.greetingCardProductHandle:', window.greetingCardProductHandle);
  
  if (!window.greetingCardProductHandle) {
    console.error('❌ Greeting card product handle not found');
    console.log('Available window properties:', Object.keys(window).filter(key => key.includes('greeting')));
    return;
  }

  try {
    const url = `/products/${window.greetingCardProductHandle}.js`;
    console.log('🔍 Fetching product from:', url);
    
    const response = await fetch(url);
    console.log('📡 Response status:', response.status);
    
    if (!response.ok) {
      console.error('❌ Product fetch failed:', response.status, response.statusText);
      return;
    }
    
    const product = await response.json();
    console.log('✅ Greeting card product loaded:', product);
    console.log('📦 Product variants:', product.variants);
    
    // Filter variants by selected language
    const languageRadio = document.querySelector('input[name="cardLanguage"]:checked');
    const selectedLanguage = languageRadio ? languageRadio.value : 'english';
    console.log('🌍 Selected language:', selectedLanguage);
    
    const filteredVariants = product.variants.filter(variant => {
      // Check both option1 and option2 for language
      const option1 = variant.option1 ? variant.option1.toLowerCase() : '';
      const option2 = variant.option2 ? variant.option2.toLowerCase() : '';
      const matches = option1.includes(selectedLanguage) || option2.includes(selectedLanguage);
      console.log(`🔍 Variant ${variant.id}: option1="${option1}", option2="${option2}", matches=${matches}`);
      return matches;
    });
    
    console.log('🎯 Filtered variants for', selectedLanguage, ':', filteredVariants);
    
    if (filteredVariants.length === 0) {
      console.warn('⚠️ No variants found for language:', selectedLanguage);
      console.log('Available variants:', product.variants.map(v => ({ id: v.id, option1: v.option1, option2: v.option2 })));
    }
    
    // Render design carousel
    renderCardDesigns(filteredVariants);
    
  } catch (error) {
    console.error('❌ Error loading greeting card designs:', error);
  }
}

function renderCardDesigns(variants) {
  console.log('🎁 renderCardDesigns called with variants:', variants);
  const carousel = document.getElementById('cardDesignCarousel');
  console.log('🎠 Carousel element:', carousel);
  
  if (!carousel) {
    console.error('❌ Card design carousel element not found');
    return;
  }
  
  // Clear carousel and reset scroll position
  carousel.innerHTML = '';
  carousel.scrollLeft = 0;
  console.log('🧹 Cleared carousel content and reset scroll');
  
  // Clear any previously selected card design
  if (window.giftMessageModal) {
    window.giftMessageModal.selectedCardVariantId = null;
    window.giftMessageModal.selectedCardDesign = null;
  }
  
  if (!variants || variants.length === 0) {
    console.warn('⚠️ No variants to render');
    carousel.innerHTML = '<p style="text-align: center; color: #666; padding: 2rem;">No greeting card designs available for this language</p>';
    return;
  }
  
  variants.forEach((variant, index) => {
    console.log(`🎨 Rendering variant ${index + 1}:`, variant);
    
    const designItem = document.createElement('div');
    designItem.className = 'card-design-item';
    designItem.dataset.variantId = variant.id;
    
    // Get variant image - try multiple sources
    let imageUrl = '/assets/no-image.png';
    if (variant.featured_image && variant.featured_image.src) {
      imageUrl = variant.featured_image.src;
    } else if (variant.image && variant.image.src) {
      imageUrl = variant.image.src;
    } else if (variant.featured_image) {
      imageUrl = variant.featured_image;
    }
    
    console.log(`🖼️ Image URL for variant ${variant.id}:`, imageUrl);
    
    // Extract just the first variant name (occasion) from the title
    const occasionName = variant.title ? variant.title.split(' / ')[0] : `Design ${index + 1}`;
    
    designItem.innerHTML = `
      <img src="${imageUrl}" alt="${variant.title || 'Greeting Card Design'}" onerror="console.error('❌ Image failed to load:', this.src); this.src='/assets/no-image.png'">
      <div class="card-design-item__label">${occasionName}</div>
    `;
    
    designItem.addEventListener('click', () => {
      console.log('🖱️ Card design clicked:', variant.id);
      selectCardDesign(variant.id, designItem);
    });
    
    carousel.appendChild(designItem);
    console.log(`✅ Added design item ${index + 1} to carousel`);
  });
  
  console.log(`🎉 Rendered ${variants.length} card designs`);
  
  // Update carousel navigation buttons visibility
  setTimeout(() => {
    const prevBtn = document.querySelector('.card-carousel-btn--prev');
    const nextBtn = document.querySelector('.card-carousel-btn--next');
    if (carousel.scrollWidth > carousel.clientWidth) {
      if (prevBtn) prevBtn.style.display = 'block';
      if (nextBtn) nextBtn.style.display = 'block';
    } else {
      if (prevBtn) prevBtn.style.display = 'none';
      if (nextBtn) nextBtn.style.display = 'none';
    }
  }, 100);
}

function selectCardDesign(variantId, element) {
  console.log('🎁 selectCardDesign called:', variantId);
  
  // Remove selected class from all items
  document.querySelectorAll('.card-design-item').forEach(item => {
    item.classList.remove('selected');
  });

  // Add selected class to clicked item
  element.classList.add('selected');
  
  // Store selection
  if (window.giftMessageModal) {
    window.giftMessageModal.selectedCardVariantId = variantId;
    window.giftMessageModal.selectedCardDesign = element;
  }
  
  console.log('Selected card design:', variantId);
}

/* ---- Premium card sub-steps: design first, then message ---- */

function resetCardSubSteps() {
  const designStep = document.getElementById('cardStepDesign');
  const messageStep = document.getElementById('cardStepMessage');
  if (designStep) designStep.style.display = '';
  if (messageStep) {
    messageStep.style.display = 'none';
    messageStep.style.minHeight = '';
  }
}

function proceedToCardMessage() {
  if (!window.giftMessageModal || !window.giftMessageModal.selectedCardVariantId) {
    alert('Please select a greeting card design');
    return;
  }

  // Summarise the chosen design on the message step
  const chosen = window.giftMessageModal.selectedCardDesign;
  const preview = document.getElementById('cardChosenPreview');
  if (preview && chosen) {
    const img = chosen.querySelector('img');
    const label = chosen.querySelector('.card-design-item__label');
    preview.innerHTML = `
      <img src="${img ? img.src : ''}" alt="${label ? label.textContent : 'Chosen design'}">
      <div class="card-chosen-preview__info">
        <span class="card-chosen-preview__name">${label ? label.textContent : 'Your design'}</span>
        <button type="button" class="card-chosen-preview__change" onclick="backToCardDesign()">Change card design</button>
      </div>`;
  }

  // Keep the modal the same size across both sub-steps
  const designStep = document.getElementById('cardStepDesign');
  const messageStep = document.getElementById('cardStepMessage');
  const designHeight = designStep.offsetHeight;
  if (designHeight) messageStep.style.minHeight = designHeight + 'px';
  designStep.style.display = 'none';
  messageStep.style.display = '';
  const textarea = document.getElementById('greetingCardMessage');
  if (textarea) textarea.focus();
}

function backToCardDesign() {
  document.getElementById('cardStepMessage').style.display = 'none';
  document.getElementById('cardStepDesign').style.display = '';
}

function proceedFromGreetingCard() {
  console.log('🎁 proceedFromGreetingCard called');

  if (!window.giftMessageModal) return;

  // Validate card selection
  if (!window.giftMessageModal.selectedCardVariantId) {
    alert('Please select a greeting card design');
    return;
  }
  
  // If we're in gift_only mode (cart context), skip upsells and add to cart directly
  if (window.giftMessageModal.mode === 'gift_only') {
    console.log('✅ In gift_only mode (cart context), skipping upsells');
    window.giftMessageModal.addAllItemsToCart(true);
    window.giftMessageModal.showSuccessAnimation();
    return;
  }
  
  // Check if upsell items are already selected (from "Add Something Extra" button)
  // Uses the same hasUpsellItemsSelected() method as the free note flow
  if (window.giftMessageModal.hasUpsellItemsSelected()) {
    console.log('✅ Upsell items already selected via "Add Something Extra" button, skipping Step 2');
    console.log('Selected items:', {
      selectedUpsells: window.selectedUpsells,
      selectedExtraItems: window.selectedExtraItems,
      modalExtraItems: window.giftMessageModal.selectedExtraItems
    });
    // Skip Step 2, add to cart directly
    window.giftMessageModal.addAllItemsToCart(true);
    window.giftMessageModal.showSuccessAnimation();
  } else if (window.upsellModalCollections && window.upsellModalCollections.length > 0) {
    console.log('Showing Step 2 upsells');
    // Go through showStep2(), NOT a hand-rolled step swap. showStep2 also runs
    // loadExtraItems(), which populates the category dropdown and binds its
    // click handler. Swapping the steps manually and calling loadExtraCollection
    // alone rendered the products but left the dropdown empty and unwired, so it
    // was dead in the greeting-card flow while working in the gift-note flow.
    window.giftMessageModal.showStep2();
  } else {
    console.log('No upsells configured, adding to cart');
    // No upsells configured, add to cart
    window.giftMessageModal.addAllItemsToCart(true);
    window.giftMessageModal.showSuccessAnimation();
  }
}

function selectPreset(message) {
  const textarea = document.getElementById('giftMessage');
  const charCountElement = document.getElementById('charCount');
  
  if (textarea) {
    // Set the preset message
    textarea.value = message;
    
    // Update character count if the modal is available
    if (window.giftMessageModal && window.giftMessageModal.messageTextarea) {
      window.giftMessageModal.updateCharCount();
    } else if (charCountElement) {
      // Fallback character count update
      charCountElement.textContent = message.length;
    }
    
    // Focus the textarea so user can edit if needed
    textarea.focus();
    
    // Move cursor to end of text
    textarea.setSelectionRange(message.length, message.length);
    
    // Optional: Add visual feedback
    const allPresetButtons = document.querySelectorAll('.preset-pill');
    allPresetButtons.forEach(btn => {
      btn.style.background = btn.getAttribute('data-original-bg') || 
                           getComputedStyle(btn).backgroundColor;
    });
    
    // Highlight the clicked button briefly
    const clickedButton = event ? event.target : null;
    if (clickedButton && clickedButton.classList.contains('preset-pill')) {
      const originalBg = getComputedStyle(clickedButton).backgroundColor;
      clickedButton.setAttribute('data-original-bg', originalBg);
      clickedButton.style.background = 'rgba(34, 197, 94, 0.15)'; // Light green feedback
      clickedButton.style.borderColor = 'rgba(34, 197, 94, 0.3)';
      clickedButton.style.color = '#16a34a';
      
      setTimeout(() => {
        clickedButton.style.background = originalBg;
        clickedButton.style.borderColor = '';
        clickedButton.style.color = '';
      }, 400);
    }
  }
}

// Helper functions for step navigation
function hideStep(stepId) {
  const step = document.getElementById(stepId);
  if (step) {
    step.style.display = 'none';
    step.classList.remove('active');
  }
}

function showStep(stepId) {
  const step = document.getElementById(stepId);
  if (step) {
    step.style.display = 'block';
    step.classList.add('active');
  }
}

// Global function to skip message from Step 0
function skipMessage() {
  console.log('🎁 skipMessage called from Step 0');
  
  if (!window.giftMessageModal) {
    console.error('❌ Gift message modal not initialized');
    return;
  }
  
  // Check if we're in cart context (gift_only mode)
  if (window.giftMessageModal.currentCartItemKey) {
    console.log('Cart context - closing modal without adding items');
    window.giftMessageModal.close();
    return;
  }
  
  // PDP context - add main product to cart without gift message
  console.log('PDP context - adding main product to cart');
  window.giftMessageModal.addAllItemsToCart(true);
  window.giftMessageModal.showSuccessAnimation();
}

// Initialize when DOM is ready, but with early event delegation
document.addEventListener('DOMContentLoaded', () => {
  console.log('DOM loaded, checking for gift message modal...');
  
  // Function to initialize the modal
  const initializeModal = () => {
    const modalElement = document.getElementById('giftMessageModal');
    console.log('Modal element found:', modalElement);
    
    // Skip if already properly initialized with a valid modal element
    if (window.giftMessageModal && window.giftMessageModal.modal) {
      console.log('Gift message modal already initialized and working, skipping...');
      return;
    }
    
    if (modalElement) {
      console.log('Initializing gift message modal...');
      window.giftMessageModal = new window.GiftMessageModal();
      console.log('Gift message modal initialized:', window.giftMessageModal);
    } else {
      console.log('Gift message modal element not found, retrying...');
      // Retry after a short delay in case the section loads later
      setTimeout(initializeModal, 500);
    }
  };
  
  // Start initialization
  initializeModal();
});

// Theme editor integration - handle section reloads
document.addEventListener('shopify:section:load', (event) => {
  console.log('Theme editor section loaded:', event.detail);
  
  // Check if the loaded section contains our modal
  if (event.target.querySelector('#giftMessageModal')) {
    console.log('Gift message modal section reloaded, re-initializing...');
    
    // Clean up existing modal if it exists
    if (window.giftMessageModal) {
      console.log('Cleaning up existing modal instance');
      // Remove any existing event listeners or cleanup if needed
    }
    
    // Re-initialize the modal
    setTimeout(() => {
      window.giftMessageModal = new window.GiftMessageModal();
      console.log('Gift message modal re-initialized for theme editor');
    }, 100);
  }
});

// Theme editor section unload - cleanup
document.addEventListener('shopify:section:unload', (event) => {
  console.log('Theme editor section unloaded:', event.detail);
  
  // Check if the unloaded section contains our modal
  if (event.target.querySelector('#giftMessageModal')) {
    console.log('Gift message modal section unloaded, cleaning up...');
    
    // Clean up modal instance
    if (window.giftMessageModal) {
      // Close modal if open
      if (window.giftMessageModal.modal && window.giftMessageModal.modal.style.display !== 'none') {
        window.giftMessageModal.close();
      }
      
      // Clear global reference
      window.giftMessageModal = null;
      console.log('Gift message modal cleaned up');
    }
  }
});

// Debug function - can be called from browser console
window.debugGiftModal = function() {
  console.log('=== Gift Modal Debug Info ===');
  console.log('Modal element:', document.getElementById('giftMessageModal'));
  console.log('window.giftMessageModal:', window.giftMessageModal);
  console.log('Modal type:', typeof window.giftMessageModal);
  if (window.giftMessageModal) {
    console.log('Modal methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(window.giftMessageModal)));
    console.log('Modal modal property:', window.giftMessageModal.modal);
    console.log('Show method exists:', typeof window.giftMessageModal.show);
  }
  console.log('=============================');
};